import React, { useMemo, useState, useEffect } from "react";
import {
  Search,
  Filter,
  LayoutGrid,
  List,
  FileText,
  Download,
  ExternalLink,
  X,
  Calendar,
  BookOpen,
  BadgeCheck,
  ChevronRight,
  Sparkles,
  ArrowRight,
  Globe,
  Library,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const cn = (...classes) => classes.filter(Boolean).join(" ");

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0 },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06, delayChildren: 0.06 } },
};

const modalWrap = {
  hidden: { opacity: 0 },
  show: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalPanel = {
  hidden: { opacity: 0, y: 18, scale: 0.98 },
  show: { opacity: 1, y: 0, scale: 1, transition: { type: "spring", stiffness: 260, damping: 24 } },
  exit: { opacity: 0, y: 12, scale: 0.985, transition: { duration: 0.15 } },
};

// ── Stat pill used in hero ──────────────────────────────────────────────────
const StatPill = ({ label, value }) => (
  <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-200/70 bg-white/60 px-5 py-3 backdrop-blur shadow-sm">
    <span className="text-xl font-black text-slate-900">{value}</span>
    <span className="mt-0.5 text-xs font-medium text-slate-500 whitespace-nowrap">{label}</span>
  </div>
);

const JournalIssues = () => {
  // ====== DATA ======
  const issues = [
    {
      id: "v3n2-2025",
      title: "Vol. 3, No. 2 (November 2025)",
      year: 2025,
      volume: 3,
      number: 2,
      month: "November",
      issn: "2786-9822",
      eissn: "3043-5382",
      publisher: "The Federal Polytechnic, Bida, Niger State, Nigeria",
      pdfUrl: "/journals/BIDA POLYMATH Vol 3 Issue 2 Nov 2025.pdf",
      coverUrl: "/journal-covers/vol3-no2-nov-2025.jpeg",
      accent: "from-indigo-600 to-blue-600",
    },
    {
      id: "v3n1-2025",
      title: "Vol. 3, No. 1 (June 2025)",
      year: 2025,
      volume: 3,
      number: 1,
      month: "June",
      issn: "2786-9822",
      eissn: "3043-5382",
      publisher: "The Federal Polytechnic, Bida, Niger State, Nigeria",
      pdfUrl: "/journals/BIDA POLYMATH Vol 3 Issue 1 June 2025.pdf",
      coverUrl: "/journal-covers/vol3-no1-june-2025.jpeg",
      accent: "from-cyan-600 to-blue-600",
    },
    {
      id: "v2n2-2024",
      title: "Vol. 2, No. 2 (December 2024)",
      year: 2024,
      volume: 2,
      number: 2,
      month: "December",
      issn: "2786-9822",
      eissn: "3043-5382",
      publisher: "The Federal Polytechnic, Bida, Niger State, Nigeria",
      pdfUrl: "/journals/BIDA VOLUME 2, NUMBER 2, DECEMBER 2024.pdf",
      coverUrl: "/journal-covers/vol2-no2-dec-2024.jpeg",
      accent: "from-emerald-600 to-teal-600",
    },
    {
      id: "v2n1-2024",
      title: "Vol. 2, No. 1 (June 2024)",
      year: 2024,
      volume: 2,
      number: 1,
      month: "June",
      issn: "2786-9822",
      eissn: "3043-5382",
      publisher: "The Federal Polytechnic, Bida, Niger State, Nigeria",
      pdfUrl: "/journals/BIDA VOLUME 2, NUMBER 1, JUNE 2024.pdf",
      coverUrl: "/journal-covers/vol2-no1-june-2024.jpeg",
      accent: "from-purple-600 to-fuchsia-600",
    },
  ];

  // ====== UI STATE ======
  const [view, setView] = useState("grid");
  const [q, setQ] = useState("");
  const [year, setYear] = useState("all");
  const [active, setActive] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  const years = useMemo(() => {
    const ys = Array.from(new Set(issues.map((i) => i.year))).sort((a, b) => b - a);
    return ["all", ...ys];
  }, [issues]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return issues
      .filter((i) => (year === "all" ? true : i.year === Number(year)))
      .filter((i) => {
        if (!query) return true;
        return (
          i.title.toLowerCase().includes(query) ||
          `${i.volume}`.includes(query) ||
          `${i.number}`.includes(query) ||
          i.month.toLowerCase().includes(query)
        );
      })
      .sort((a, b) => b.year - a.year || b.volume - a.volume || b.number - a.number);
  }, [issues, q, year]);

  const latest = filtered?.[0] || issues[0];

  const openIssue = (issue) => setActive(issue);
  const closeIssue = () => setActive(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && closeIssue();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    document.body.style.overflow = active ? "hidden" : "auto";
    return () => (document.body.style.overflow = "auto");
  }, [active]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      {/* ====== REWORKED HERO ====== */}
      <header className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 pt-[120px] md:pt-[140px]">
        {/* ── Background decorations ── */}
        <div className="pointer-events-none absolute inset-0">
          {/* Grid overlay */}
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,.6) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.6) 1px,transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />
          {/* Radial glow blobs */}
          <motion.div
            className="absolute -top-32 -left-20 h-[420px] w-[420px] rounded-full bg-blue-500/20 blur-3xl"
            animate={{ x: [0, 20, -8, 0], y: [0, 12, 24, 0] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute top-0 right-0 h-[480px] w-[480px] rounded-full bg-indigo-600/15 blur-3xl"
            animate={{ x: [0, -18, 12, 0], y: [0, 20, -10, 0] }}
            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute bottom-0 left-1/3 h-[320px] w-[320px] rounded-full bg-purple-600/10 blur-3xl"
            animate={{ x: [0, 14, -20, 0], y: [0, -10, 16, 0] }}
            transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }}
          />
          {/* bottom fade to page bg */}
          <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-slate-50 to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-6 pb-0">
          <motion.div variants={stagger} initial="hidden" animate="show" className="flex flex-col gap-12">

            {/* ── Top badge row ── */}
            <motion.div variants={fadeUp} className="flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-1.5 text-xs font-semibold text-white/80 backdrop-blur">
                <BadgeCheck className="h-3.5 w-3.5 text-blue-400" />
                Peer-Reviewed · Multi-Disciplinary
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/8 px-4 py-1.5 text-xs font-semibold text-white/60 backdrop-blur">
                <Globe className="h-3.5 w-3.5" />
                Open Access
              </span>
            </motion.div>

            {/* ── Main two-column layout ── */}
            <div className="grid lg:grid-cols-12 gap-10 items-end pb-12">

              {/* LEFT: Branding + headline + CTAs */}
              <motion.div variants={fadeUp} className="lg:col-span-6 flex flex-col gap-6">
                {/* Journal mark */}
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg shadow-blue-900/40">
                    <Library className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="text-xs font-bold tracking-widest text-blue-400 uppercase">PJHST</div>
                    <div className="text-sm font-semibold text-white/70">Federal Polytechnic, Bida</div>
                  </div>
                </div>

                {/* Headline */}
                <div>
                  <h1 className="text-4xl md:text-5xl font-black tracking-tight text-white leading-[1.1]">
                    Polymath Journal of{" "}
                    <span className="bg-gradient-to-r from-blue-400 via-indigo-300 to-purple-400 bg-clip-text text-transparent">
                      Humanities, Science
                    </span>{" "}
                    &amp; Technology
                  </h1>
                  <p className="mt-4 text-base text-white/60 max-w-lg leading-relaxed">
                    Browse, read online, and download every issue — all in one clean, searchable library experience.
                  </p>
                </div>

                {/* ISSN badges */}
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: "Print ISSN", val: "2786-9822" },
                    { label: "E-ISSN", val: "3043-5382" },
                  ].map(({ label, val }) => (
                    <span
                      key={val}
                      className="rounded-lg border border-white/10 bg-white/8 px-3 py-1.5 text-xs text-white/60 backdrop-blur"
                    >
                      {label}:{" "}
                      <span className="font-bold text-white/90 tracking-wide">{val}</span>
                    </span>
                  ))}
                </div>

                {/* CTA buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => latest && openIssue(latest)}
                    className="group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-600 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-blue-900/40 hover:shadow-blue-800/50 transition-all duration-200"
                  >
                    <span className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition" />
                    <FileText className="h-4 w-4" />
                    Read Latest Issue
                    <Sparkles className="h-4 w-4 group-hover:rotate-12 transition" />
                  </button>

                  <a
                    href="https://fedpolybida.edu.ng/polymath-journal"
                    target="_blank"
                    rel="noreferrer"
                    className="group inline-flex items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/8 px-6 py-3.5 text-sm font-semibold text-white/80 backdrop-blur hover:bg-white/15 transition"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Journal Website
                    <ArrowRight className="h-4 w-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition" />
                  </a>
                </div>

                {/* Stat pills */}
                <div className="flex gap-3 flex-wrap">
                  <StatPill value="3" label="Volumes" />
                  <StatPill value="4" label="Issues" />
                  <StatPill value="2023–" label="Est." />
                  <StatPill value="PDF" label="Open Access" />
                </div>
              </motion.div>

              {/* RIGHT: Elevated latest-issue card */}
              <motion.div variants={fadeUp} className="lg:col-span-6 flex justify-end">
                <div className="w-full max-w-md">
                  {/* Floating label */}
                  <div className="mb-3 flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/10 px-3 py-1 text-xs font-semibold text-white/70 backdrop-blur">
                      <BookOpen className="h-3.5 w-3.5 text-blue-400" />
                      Latest Issue
                    </span>
                    <div className="h-px flex-1 bg-white/10" />
                  </div>

                  <motion.div
                    whileHover={{ y: -4 }}
                    transition={{ type: "spring", stiffness: 300, damping: 22 }}
                    className="rounded-3xl border border-white/10 bg-white/8 backdrop-blur-xl shadow-2xl shadow-black/30 overflow-hidden"
                  >
                    {/* Accent bar */}
                    <div className={cn("h-1 bg-gradient-to-r", latest?.accent || "from-indigo-500 to-blue-500")} />

                    <div className="p-5 flex gap-4">
                      {/* Cover image */}
                      <motion.div
                        whileHover={{ rotate: -1.5, scale: 1.02 }}
                        className="relative w-32 h-44 rounded-2xl overflow-hidden border border-white/10 bg-slate-800 flex-shrink-0 shadow-lg"
                      >
                        <img
                          src={latest?.coverUrl}
                          alt={latest?.title}
                          className="w-full h-full object-cover"
                          onError={(e) => (e.currentTarget.style.display = "none")}
                        />
                        {/* sheen */}
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/10 to-transparent" />
                      </motion.div>

                      {/* Info */}
                      <div className="min-w-0 flex flex-col gap-2">
                        <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-400">
                          <Calendar className="h-3.5 w-3.5" />
                          {latest?.month} {latest?.year}
                        </div>

                        <h3 className="text-base font-black text-white leading-snug">
                          Vol {latest?.volume}, No {latest?.number}
                        </h3>

                        <p className="text-sm text-white/50 leading-relaxed line-clamp-3">
                          {latest?.publisher}
                        </p>

                        {/* Tags */}
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {["Peer-Reviewed", "PDF Available", "Open Access"].map((t) => (
                            <span
                              key={t}
                              className="rounded-full border border-white/10 bg-white/8 px-2.5 py-0.5 text-[10px] font-semibold text-white/60"
                            >
                              {t}
                            </span>
                          ))}
                        </div>

                        {/* Action buttons */}
                        <div className="mt-auto flex gap-2 pt-2">
                          <button
                            onClick={() => latest && openIssue(latest)}
                            className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 px-4 py-2 text-xs font-bold text-white shadow hover:brightness-110 transition"
                          >
                            <FileText className="h-3.5 w-3.5" />
                            Read
                          </button>
                          <a
                            href={latest?.pdfUrl}
                            download
                            className="inline-flex items-center gap-1.5 rounded-xl border border-white/15 bg-white/10 px-4 py-2 text-xs font-semibold text-white/80 hover:bg-white/20 transition"
                          >
                            <Download className="h-3.5 w-3.5" />
                            Download
                          </a>
                        </div>
                      </div>
                    </div>

                    {/* Tip strip */}
                    <div className="border-t border-white/8 bg-white/5 px-5 py-3 flex items-center gap-2">
                      <Sparkles className="h-3.5 w-3.5 text-indigo-400 flex-shrink-0" />
                      <p className="text-xs text-white/50">
                        Tip: Search <span className="font-semibold text-white/70">"Vol 3"</span> or filter by year to find any issue instantly.
                      </p>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>

            {/* ====== CONTROLS (sticky) — now sits AT the bottom of the hero, spanning full width ====== */}
            <motion.div
              variants={fadeUp}
              className={cn(
                "rounded-t-2xl border-x border-t border-slate-200 bg-white/95 shadow-sm backdrop-blur -mx-6 px-6 pt-4 pb-4",
                scrolled ? "sticky top-20 z-40" : ""
              )}
            >
              <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                    <Search className="h-4 w-4 text-slate-400 flex-shrink-0" />
                    <input
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Search by volume, number, month, year…"
                      className="w-full bg-transparent outline-none text-slate-800 placeholder:text-slate-400 text-sm"
                    />
                    {q && (
                      <button onClick={() => setQ("")}>
                        <X className="h-4 w-4 text-slate-400 hover:text-slate-700 transition" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 items-center">
                  <div className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5">
                    <Filter className="h-4 w-4 text-slate-500" />
                    <select
                      value={year}
                      onChange={(e) => setYear(e.target.value)}
                      className="bg-transparent outline-none text-sm font-semibold text-slate-800"
                    >
                      {years.map((y) => (
                        <option key={y} value={y}>
                          {y === "all" ? "All Years" : y}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1">
                    <button
                      onClick={() => setView("grid")}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition",
                        view === "grid" ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
                      )}
                    >
                      <LayoutGrid className="h-4 w-4" />
                      Grid
                    </button>
                    <button
                      onClick={() => setView("list")}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition",
                        view === "list" ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
                      )}
                    >
                      <List className="h-4 w-4" />
                      List
                    </button>
                  </div>
                </div>
              </div>

              <div className="max-w-7xl mx-auto mt-2.5 text-xs text-slate-500">
                Showing <span className="font-semibold text-slate-800">{filtered.length}</span> issue{filtered.length !== 1 ? "s" : ""}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </header>

      {/* ====== CONTENT ====== */}
      <main className="max-w-7xl mx-auto px-6 pb-16 mt-8">
        {filtered.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-sm"
          >
            <div className="mx-auto w-fit rounded-2xl bg-slate-100 p-4">
              <Search className="h-6 w-6 text-slate-600" />
            </div>
            <h3 className="mt-4 text-xl font-black text-slate-900">No results found</h3>
            <p className="mt-2 text-slate-600">Try a different search term or clear the filters.</p>
          </motion.div>
        ) : view === "grid" ? (
          <motion.div variants={stagger} initial="hidden" animate="show" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((issue) => (
              <motion.button
                key={issue.id}
                variants={fadeUp}
                whileHover={{ y: -6 }}
                whileTap={{ scale: 0.985 }}
                onClick={() => openIssue(issue)}
                className="group text-left rounded-3xl border border-slate-200 bg-white shadow-sm hover:shadow-xl transition overflow-hidden"
              >
                <div className={`h-1.5 bg-gradient-to-r ${issue.accent}`} />
                <div className="p-5">
                  <div className="flex items-start gap-4">
                    <motion.div
                      whileHover={{ rotate: -1.1 }}
                      className="relative w-24 h-32 rounded-2xl overflow-hidden border border-slate-200 bg-slate-100 flex-shrink-0"
                    >
                      <img
                        src={issue.coverUrl}
                        alt={issue.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          e.currentTarget.parentElement.classList.add("items-center", "justify-center");
                          e.currentTarget.parentElement.innerHTML = `<div class="text-center px-2"><div class="text-xs font-semibold text-slate-600">PJHST</div><div class="mt-1 text-[10px] text-slate-500">Cover unavailable</div></div>`;
                        }}
                      />
                      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(120deg,transparent,rgba(255,255,255,.25),transparent)] translate-x-[-120%] group-hover:translate-x-[120%] transition duration-700" />
                    </motion.div>

                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-slate-500 flex items-center gap-2">
                        <Calendar className="h-3.5 w-3.5" />
                        {issue.month} {issue.year}
                      </div>
                      <h3 className="mt-1 text-lg font-black text-slate-900 group-hover:text-slate-700 transition line-clamp-2">
                        {issue.title}
                      </h3>
                      <p className="mt-2 text-sm text-slate-600 line-clamp-2">
                        Multi-disciplinary journal of The Federal Polytechnic, Bida.
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">Vol {issue.volume}</span>
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">No {issue.number}</span>
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">PDF</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 flex items-center justify-between">
                    <div className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800">
                      <FileText className="h-4 w-4" />
                      Open Issue
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-slate-700 group-hover:translate-x-0.5 transition" />
                  </div>
                </div>
              </motion.button>
            ))}
          </motion.div>
        ) : (
          <motion.div variants={stagger} initial="hidden" animate="show" className="space-y-3">
            {filtered.map((issue) => (
              <motion.div
                key={issue.id}
                variants={fadeUp}
                className="rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition"
              >
                <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-16 h-20 rounded-xl overflow-hidden border border-slate-200 bg-slate-100 flex-shrink-0">
                      <img
                        src={issue.coverUrl}
                        alt={issue.title}
                        className="w-full h-full object-cover"
                        onError={(e) => (e.currentTarget.style.display = "none")}
                      />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-slate-500">
                        {issue.month} {issue.year} • Vol {issue.volume}, No {issue.number}
                      </div>
                      <div className="text-base font-black text-slate-900 truncate">{issue.title}</div>
                      <div className="text-sm text-slate-600 truncate">{issue.publisher}</div>
                    </div>
                  </div>
                  <div className="sm:ml-auto flex gap-2">
                    <button
                      onClick={() => openIssue(issue)}
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 transition"
                    >
                      <FileText className="h-4 w-4" />
                      Read
                    </button>
                    <a
                      href={issue.pdfUrl}
                      download
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50 transition"
                    >
                      <Download className="h-4 w-4" />
                      Download
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </main>

      {/* ====== READER MODAL ====== */}
      <AnimatePresence>
        {active && (
          <motion.div
            variants={modalWrap}
            initial="hidden"
            animate="show"
            exit="exit"
            className="fixed inset-0 z-50"
            role="dialog"
            aria-modal="true"
          >
            <motion.div
              className="absolute inset-0 bg-black/55"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeIssue}
            />
            <div className="absolute inset-0 flex items-center justify-center p-4">
              <motion.div variants={modalPanel} className="w-full max-w-6xl h-[88vh] rounded-3xl bg-white shadow-2xl overflow-hidden border border-slate-200">
                <div className={`h-1.5 bg-gradient-to-r ${active.accent}`} />
                <div className="px-5 py-4 border-b border-slate-200 flex items-start gap-3">
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-slate-500">
                      PJHST • ISSN {active.issn} • EISSN {active.eissn}
                    </div>
                    <h3 className="text-lg md:text-xl font-black text-slate-900 truncate">{active.title}</h3>
                    <div className="text-sm text-slate-600 truncate">{active.publisher}</div>
                  </div>
                  <div className="ml-auto flex gap-2">
                    <a
                      href={active.pdfUrl}
                      download
                      className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 transition"
                    >
                      <Download className="h-4 w-4" />
                      Download PDF
                    </a>
                    <button
                      onClick={closeIssue}
                      className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white p-2 hover:bg-slate-50 transition"
                      aria-label="Close"
                    >
                      <X className="h-5 w-5 text-slate-700" />
                    </button>
                  </div>
                </div>
                <div className="h-[calc(88vh-72px)] bg-slate-100">
                  <iframe title={active.title} src={`${active.pdfUrl}#view=fitH`} className="w-full h-full" />
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ====== FOOTER ====== */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <div className="text-sm text-slate-600">
            © {new Date().getFullYear()} • Polymath Journal of Humanities, Science & Technology (PJHST)
          </div>
          <div className="text-sm text-slate-600">ISSN 2786-9822 • EISSN 3043-5382</div>
        </div>
      </footer>
    </div>
  );
};

export default JournalIssues;